/**
 * M10 P5a4a (hull tiers spec §3.2, §6.1; plan 2026-10-17-m10-p5a §3.1): the per-hull fit resolver. One answer to
 * "how much hold, shield and power does this ship have": with a `hulls.json fit` on the ship's hull row, the hull's
 * rating × the band's Mk fraction (`hull-lines.json sizes.<band>.mkFractions[mk − 1]`: S/M {0.4, 0.7, 1}, L/XL
 * {0.7, 0.85, 1}); with no `fit` (every shipped row today), the fitted module's absolute stat, exactly as before.
 * A patterned module keeps its pattern's shift on the rated value (rating × fraction × patterned / base stat), so a
 * base module gives the plain product. Thrust is never hull-rated (plan Q3). Not per-frame code (fit changes,
 * switches and loads only).
 */
import { z } from 'zod'
import { moduleStats, resolveModule } from '../data/m2'
import { moduleId, parseModuleId, WARE_IDS, type M2Data, type ModuleId, type SlotId, type WareId } from '../data/m2Schemas'
import hullsJson from '../data/strategic/hulls.json'
import strategicWaresJson from '../data/strategic/wares.json'
import { HULL_BAND_IDS, loadHullLines, type HullBand } from '../strategic/hullLines'
import { CARGO_CLASSES, cargoClassSchema, hullFitSchema, type CargoClass, type HullFit } from '../strategic/schemas'
import { GOOD_IDS, GOOD_MASS_T, type GoodId, type Goods } from './parts'
import { processorHulls } from './processing'
import type { CargoComponent } from './types'

/** The slots whose stat a hull rates: the cargo racks (hold t), the shield (shield) and the power plant (power). */
export type RatedSlot = 'cargo' | 'shield' | 'power'

/** The fields the resolver reads of a hull row (`hulls.json`, validated here; the rest of the row is ignored). */
const ratedHullSchema = z.object({
  id: z.string(),
  band: z.enum(HULL_BAND_IDS).optional(),
  fit: hullFitSchema.optional(),
})

/**
 * Ship def id → hull id (the `hulls.json shipDef` column; the same map as `hulks/restore.ts HULL_OF_DEF`, built here so
 * `career/ops.ts` can read the hold multipliers without importing `hulks/restore`, which imports `ops`).
 */
const HULL_OF_DEF: ReadonlyMap<string, string> = new Map(hullsJson.hulls.map((h) => [h.shipDef, h.id]))

/** The hull row a ship def flies, or null when no hull lists it. */
export function hullOfDef(defId: string): string | null {
  return HULL_OF_DEF.get(defId) ?? null
}

/** What the resolver reads: each hull's band and `fit` by hull id, and the Mk fractions per band. */
export interface HullFitData {
  hulls: ReadonlyMap<string, { band?: HullBand; fit?: HullFit }>
  mkFractions: Readonly<Record<HullBand, readonly [number, number, number]>>
  /**
   * M10 P5a6a (plan Q4 merged rec): hull ids with an on-site processor. On such a hull raw bulk tonnes count 1× and
   * only processed tonnes (`CargoState.processed`) count ÷ `holdMult.bulk`. M10 P5a12d: the shipped set is
   * `processing.json hulls` when `enabled` (`processorHulls`), else empty (shipped: off until W6).
   */
  processors: ReadonlySet<string>
}

/** Builds the resolver data from raw `hulls.json` rows and parsed `hull-lines.json` sizes (tests pass copies). */
export function hullFitDataFrom(
  rawHulls: readonly unknown[],
  sizes: Readonly<Record<HullBand, { mkFractions: readonly [number, number, number] }>>,
  processors: readonly string[] = [],
): HullFitData {
  const hulls = new Map<string, { band?: HullBand; fit?: HullFit }>()
  for (const raw of rawHulls) {
    const row = ratedHullSchema.parse(raw)
    const entry: { band?: HullBand; fit?: HullFit } = {}
    if (row.band !== undefined) entry.band = row.band
    if (row.fit !== undefined) entry.fit = row.fit
    hulls.set(row.id, entry)
  }
  return {
    hulls,
    mkFractions: { S: sizes.S.mkFractions, M: sizes.M.mkFractions, L: sizes.L.mkFractions, XL: sizes.XL.mkFractions },
    processors: new Set(processors),
  }
}

let shipped: HullFitData | null = null

/** The shipped resolver data (`hulls.json`, `hull-lines.json` and the `processing.json` processor set, parsed once). */
export function hullFitData(): HullFitData {
  shipped ??= hullFitDataFrom(hullsJson.hulls, loadHullLines().sizes, processorHulls())
  return shipped
}

/** M10 P5a12d: does ship def `defId` fly a processor hull (`HullFitData.processors`)? */
export function isProcessorDef(defId: string, hulls: HullFitData = hullFitData()): boolean {
  const hullId = hullOfDef(defId)
  return hullId !== null && hulls.processors.has(hullId)
}

/** The module stat a slot carries today (absolute: hold t, shield multiplier, power budget; `powerBudget`'s 0 for a non-plant). */
function moduleStat(data: Pick<M2Data, 'modules'>, slot: RatedSlot, id: ModuleId): number {
  if (slot === 'cargo') return moduleStats(data, 'cargo', id).capacityT
  if (slot === 'shield') return moduleStats(data, 'shield', id).shieldMult
  const m = resolveModule(data, id)
  return m.kind === 'power' ? m.stats.budget : 0
}

/** The hull rating of a slot. */
function rating(fit: HullFit, slot: RatedSlot): number {
  if (slot === 'cargo') return fit.holdT
  if (slot === 'shield') return fit.shieldT
  return fit.powerT
}

/**
 * The rated stat of `slot` on ship def `defId` with `fitted`: `fit.<holdT|shieldT|powerT> × mkFractions[mk − 1]`
 * when the def's hull row has a `fit` (and a band), else the fitted module's absolute stat (today's numbers).
 */
export function ratedStat(
  defId: string,
  slot: RatedSlot,
  fitted: Readonly<Record<SlotId, ModuleId>>,
  data: Pick<M2Data, 'modules'>,
  hulls: HullFitData = hullFitData(),
): number {
  const id = fitted[slot]
  const absolute = moduleStat(data, slot, id)
  const hullId = hullOfDef(defId)
  const row = hullId === null ? undefined : hulls.hulls.get(hullId)
  if (row?.fit === undefined || row.band === undefined) return absolute
  const { kind, mk } = parseModuleId(id)
  const base = moduleStat(data, slot, moduleId(kind, mk))
  const patternShift = base > 0 ? absolute / base : 1
  return rating(row.fit, slot) * hulls.mkFractions[row.band][mk - 1] * patternShift
}

/** The hold (tonnes) of ship def `defId` with `fitted` (`ratedStat` of the cargo slot). */
export function holdCapacityT(
  defId: string,
  fitted: Readonly<Record<SlotId, ModuleId>>,
  data: Pick<M2Data, 'modules'>,
  hulls: HullFitData = hullFitData(),
): number {
  return ratedStat(defId, 'cargo', fitted, data, hulls)
}

// ---------------------------------------------------------------------------------------------------------------
// M10 P5a6a (spec §3.1, §3.2; plan §3.2 card P5a6a, Q4 merged rec): the hold counts each ware by its cargo class.
// A tonne of class `c` takes `1 / fit.holdMult[c]` tonnes of hold (absent = 1), so a bulk ×1.5 hull carries 1.5×
// the ore. On a processor hull raw bulk tonnes count 1× and only processed tonnes count ÷ `holdMult.bulk` (the
// compaction). Every shipped row has no `fit`, so every multiplier is 1 and every count is today's.
// ---------------------------------------------------------------------------------------------------------------

/** The `cargoClass` column of the strategic ware catalogue (validated; the rest of the row is ignored). */
const wareClassSchema = z.object({ id: z.string(), cargoClass: cargoClassSchema.default('container') })

const CLASS_OF: ReadonlyMap<string, CargoClass> = new Map(
  z.object({ wares: z.array(wareClassSchema) }).parse(strategicWaresJson).wares.map((w) => [w.id, w.cargoClass]),
)

/**
 * The cargo class of a ware or good id (`wares.json cargoClass`). Ids that are no strategic ware (relay parts,
 * Rush spares) and components ride as `container`.
 */
export function cargoClassOf(id: string): CargoClass {
  return CLASS_OF.get(id) ?? 'container'
}

/** The class index of each M2 ware (`WARE_IDS` order), for the per-frame collector. */
const WARE_CLASS: readonly CargoClass[] = WARE_IDS.map((w) => cargoClassOf(w))

/** M10 P5a12d: which M2 wares are raw bulk ore (`WARE_IDS` order; the processor works these). */
export const BULK_WARE_MASK: readonly boolean[] = WARE_CLASS.map((c) => c === 'bulk')

/** The `fit` of ship def `defId`'s hull row, or null (M10 P5a6b H5: returns the row's fit, allocating nothing). */
function fitOfDef(defId: string, hulls: HullFitData): HullFit | null {
  const hullId = hullOfDef(defId)
  if (hullId === null) return null
  return hulls.hulls.get(hullId)?.fit ?? null
}

/** The hold multiplier of class `cls` on ship def `defId` (`fit.holdMult[cls]`; 1 when absent or the row has no `fit`). */
export function holdMultOf(defId: string, cls: CargoClass, hulls: HullFitData = hullFitData()): number {
  const m = fitOfDef(defId, hulls)?.holdMult[cls]
  return m !== undefined && m > 0 ? m : 1
}

/** The multiplier on raw (unprocessed) tonnes of `cls`: `holdMultOf`, except 1 for bulk on a processor hull (Q4). */
export function rawHoldMultOf(defId: string, cls: CargoClass, hulls: HullFitData = hullFitData()): number {
  if (cls === 'bulk') {
    const hullId = hullOfDef(defId)
    if (hullId !== null && hulls.processors.has(hullId)) return 1
  }
  return holdMultOf(defId, cls, hulls)
}

/** Does ship def `defId` count any class at other than 1×? (false for every shipped def). */
export function hasHoldMult(defId: string, hulls: HullFitData = hullFitData()): boolean {
  const f = fitOfDef(defId, hulls)
  if (f === null) return false
  for (const c of CARGO_CLASSES) {
    const m = f.holdMult[c]
    if (m !== undefined && m > 0 && m !== 1) return true
  }
  return false
}

/**
 * Writes the raw-tonne hold multiplier of each M2 ware (`WARE_IDS` index) on `defId` into `out` (the collector's
 * per-ware divisor; `CareerMirror.holdMult`). Allocates nothing.
 */
export function holdMultInto(defId: string, out: Float64Array, hulls: HullFitData = hullFitData()): Float64Array {
  for (let i = 0; i < WARE_CLASS.length && i < out.length; i++) out[i] = rawHoldMultOf(defId, WARE_CLASS[i], hulls)
  return out
}

/** The hold readout's class note: "bulk ×1.5" (classes ≠ 1, in `CARGO_CLASSES` order), or null when every class is 1×. */
export function holdMultLabel(defId: string, hulls: HullFitData = hullFitData()): string | null {
  const f = fitOfDef(defId, hulls)
  if (f === null) return null
  const parts: string[] = []
  for (const c of CARGO_CLASSES) {
    const m = f.holdMult[c]
    if (m !== undefined && m > 0 && m !== 1) parts.push(`${c} ×${m}`)
  }
  return parts.length === 0 ? null : parts.join(', ')
}

/** The hold contents the class-weighted count reads (a `CargoState` or a pod's `CargoContents`). */
export interface WeighedHold {
  readonly wares: Readonly<Record<WareId, number>>
  readonly parts?: Readonly<Goods>
  readonly goods?: Readonly<Goods>
  readonly components: readonly CargoComponent[]
  readonly processed?: Readonly<Partial<Record<WareId, number>>>
}

/**
 * Tonnes of hold `cargo` takes on ship def `defId`: each ware's raw tonnes ÷ `rawHoldMultOf` plus its processed
 * tonnes ÷ `holdMultOf`, the goods block(s) by their ware's class, and each component's mass as `container`. With
 * every multiplier 1 this is exactly `waresT + goodsT + Σ massT`, in the same order (byte-identical).
 */
export function holdUsedT(cargo: WeighedHold, defId: string, hulls: HullFitData = hullFitData()): number {
  let wares = 0
  for (const w of WARE_IDS) {
    const t = cargo.wares[w]
    const p = Math.min(cargo.processed?.[w] ?? 0, t)
    const cls = cargoClassOf(w)
    wares += p > 0 ? (t - p) / rawHoldMultOf(defId, cls, hulls) + p / holdMultOf(defId, cls, hulls) : t / rawHoldMultOf(defId, cls, hulls)
  }
  // M10 P5a6b H5: the two goods blocks without an array literal (same order: parts, then goods).
  const goods = goodsHoldT(cargo.goods, defId, hulls, goodsHoldT(cargo.parts, defId, hulls, 0))
  let t = wares + goods
  const container = holdMultOf(defId, 'container', hulls)
  for (const c of cargo.components) t += c.massT / container
  return t
}

/** Adds the class-weighted hold tonnes of goods `block` on `defId` to `acc` (allocates nothing). */
function goodsHoldT(block: Readonly<Goods> | undefined, defId: string, hulls: HullFitData, acc: number): number {
  if (block === undefined) return acc
  let goods = acc
  for (const g of GOOD_IDS) goods += ((block[g] ?? 0) * GOOD_MASS_T[g]) / holdMultOf(defId, cargoClassOf(g), hulls)
  return goods
}

// ---------------------------------------------------------------------------------------------------------------
// M10 P5a6b (W2 review H2/H3): raw tonnes → hold tonnes for one ware or good on one hull, for every caller that
// compares a raw amount with weighted free space. Division by 1 is exact, so every shipped def gives the raw number.
// ---------------------------------------------------------------------------------------------------------------

/** Hold tonnes `tonnes` of (raw) ware `ware` take on ship def `defId`: `tonnes ÷ rawHoldMultOf` (as `addOre`). */
export function wareHoldT(defId: string, ware: string, tonnes: number, hulls: HullFitData = hullFitData()): number {
  return tonnes / rawHoldMultOf(defId, cargoClassOf(ware), hulls)
}

/** Raw tonnes of ware `ware` that `holdT` tonnes of hold carry on `defId` (the inverse of `wareHoldT`). */
export function wareTOfHold(defId: string, ware: string, holdT: number, hulls: HullFitData = hullFitData()): number {
  return holdT * rawHoldMultOf(defId, cargoClassOf(ware), hulls)
}

/** Hold tonnes `qty` units of good `good` take on `defId`: `qty × mass ÷ holdMultOf` (as `holdUsedT`'s goods). */
export function goodHoldT(defId: string, good: GoodId, qty: number, hulls: HullFitData = hullFitData()): number {
  return (qty * GOOD_MASS_T[good]) / holdMultOf(defId, cargoClassOf(good), hulls)
}

/** Hold tonnes component mass `massT` takes on `defId` (components ride as `container`). */
export function containerHoldT(defId: string, massT: number, hulls: HullFitData = hullFitData()): number {
  return massT / holdMultOf(defId, 'container', hulls)
}

/** Hold tonnes goods block `goods` takes on `defId` (`GOOD_IDS` order; Σ units × mass on every shipped hull). */
export function goodsBlockHoldT(defId: string, goods: Readonly<Goods>, hulls: HullFitData = hullFitData()): number {
  return goodsHoldT(goods, defId, hulls, 0)
}

/** The hold readout `text` with the hull's class note appended ("120 / 360 t · bulk ×1.5"; `text` unchanged at 1×). */
export function holdReadout(text: string, defId: string, hulls: HullFitData = hullFitData()): string {
  const note = holdMultLabel(defId, hulls)
  return note === null ? text : `${text} · ${note}`
}
